import { SharedResizeObserver } from '@angular/cdk/observers/private';
import {
    ConnectedPosition,
    FlexibleConnectedPositionStrategy,
    Overlay,
    OverlayConfig,
    OverlayRef,
    ScrollDispatcher,
    ScrollStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    DestroyRef,
    Directive,
    ElementRef,
    InjectionToken,
    Injector,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewContainerRef,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { SharedIntersectionObserver } from './tooltip.utils';

export const DS_TOOLTIP_POSITION_ARRAY = ['left', 'top', 'right', 'bottom'] as const;
export type DsTooltipPosition = (typeof DS_TOOLTIP_POSITION_ARRAY)[number];
export const DS_ARROW_POSITION_ARRAY = ['start', 'middle', 'end'] as const;
export type DsArrowPosition = (typeof DS_ARROW_POSITION_ARRAY)[number];

export interface OverlayRefWithData {
    ref: OverlayRef;
    data: {
        position: DsTooltipPosition;
        arrowPosition: DsArrowPosition;
    };
}

export const OVERLAY_REF_WITH_DATA_TOKEN = new InjectionToken<OverlayRefWithData>('OverlayRef with data');

@Directive({
    selector: '[dsTooltipTriggerFor]',
    standalone: true,
    host: {
        '(click)': 'toggleTooltip($event)',
    },
})
export class DsTooltipTrigger implements OnInit, OnDestroy, AfterViewInit {
    private overlay = inject(Overlay);
    private injector = inject(Injector);
    private elementRef = inject(ElementRef);
    private ngZone = inject(NgZone);
    private vcr = inject(ViewContainerRef);

    private readonly destroyRef = inject(DestroyRef);
    private sharedIntersectionObserver = inject(SharedIntersectionObserver);
    private sharedResizeObserver = inject(SharedResizeObserver);
    private _scrollDispatcher = inject(ScrollDispatcher);

    // internal signals
    private _isVisible = signal(false);
    private _isShown = signal(false);
    private _tooltipToggle = signal(false);

    readonly closeOutsideViewport = input(false, { transform: booleanAttribute });
    readonly autoOpen = input(false, { transform: booleanAttribute });
    readonly fullWidth = input(false, { transform: booleanAttribute });
    readonly position = input<DsTooltipPosition>('top');
    readonly arrowPosition = input<DsArrowPosition>('start');

    /* We support full width only for top and bottom positioning
     *  In case there is the requirement in future to show full width also for left and right,
     * then following question have to be answered:
     * - where to place tooltip on full width as it will go over the trigger
     * Then, one can remove this function and override left and right position in media query to follow logic of top or bottom
     * Then, one has to think, on how adjustArrowPositions know, that it is shown full width and where the arrow should be placed
     * */
    private _position = computed(() => {
        if (this.fullWidth() && this.position() !== 'top' && this.position() !== 'bottom') {
            console.warn('Invalid position for full width, defaulting to top');
            return 'top';
        } else {
            return this.position();
        }
    });

    readonly autoCloseTime = input(10_000);
    @Input('dsTooltipTriggerFor') content!: TemplateRef<any>;
    readonly closeOnScroll = input(false, { transform: booleanAttribute });
    readonly hasBackdrop = input(false, { transform: booleanAttribute });
    readonly closeOnInteraction = input(false, { transform: booleanAttribute });
    readonly tooltipCustomClass = input('');

    private globalClickListener!: () => void;

    @Input()
    set tooltipToggle(value: boolean) {
        this._tooltipToggle.set(value);
        if (!this._overlayRef) {
            return;
        }
        if (value) {
            this.openTooltip();
        } else {
            this.closeTooltip();
        }
    }

    get tooltipToggle(): boolean {
        return this._tooltipToggle();
    }

    readonly tooltipToggleChange = output<{ isTooltipOpen: boolean; source: string }>();

    private autoCloseTimeout?: ReturnType<typeof setTimeout>;
    private backdropSubscription?: Subscription;
    private overlaySubscriptions = new Subscription();
    private attachmentsSubscription?: Subscription;
    private detachmentsSubscription?: Subscription;
    private resizeObserver!: ResizeObserver;
    private _lastObservedWidth = 0;
    _overlayRef?: OverlayRef;

    constructor() {
        /* This effect handles auto open true to open the first time tooltip is shown */
        effect(() => {
            const isVisible = this._isVisible();
            const isAutoOpen = this.autoOpen();
            const isShown = this._isShown();
            const tooltipToggle = this._tooltipToggle();

            const firstTimeAutoOpen = isAutoOpen && !isShown;

            if (isVisible && (tooltipToggle || firstTimeAutoOpen)) {
                // we also have to set the toggle to true if it is not opened but auto open is true
                if (!tooltipToggle) {
                    this._tooltipToggle.set(true);
                }
                // Update signal before opening
                if (!isShown) {
                    untracked(() => {
                        this._isShown.set(true);
                    });
                    this.initializeAutoOpen(isAutoOpen);
                }
            }
        });

        /* This effect closes the tooltip outside viewport if it is true */
        effect(() => {
            const isVisible = this._isVisible();
            const closeOutsideViewport = untracked(() => this.closeOutsideViewport());
            const isOpen = untracked(() => this._tooltipToggle());
            if (closeOutsideViewport && isOpen && !isVisible) {
                this.closeTooltip();
                this._tooltipToggle.set(false);
            }
        });
    }

    ngOnInit(): void {
        this._overlayRef = this.createOverlay();

        this.sharedResizeObserver
            .observe(this._overlayRef.overlayElement, {})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    this.adjustArrowPosition();
                });
            });

        this.listenToOverlayState();

        /* Register observer to listen for viewport visibility*/
        this.sharedIntersectionObserver
            .observe(this.elementRef.nativeElement, { threshold: 1 })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((changes) => {
                this._isVisible.set(changes[0].isIntersecting);
            });

        if (this.closeOnScroll()) {
            const scrollableAncestors = this._scrollDispatcher.getAncestorScrollContainers(this.elementRef);

            const onAncestorScroll = () => {
                this.ngZone.run(() => this.closeTooltip());
            };

            scrollableAncestors.forEach((scrollable) => {
                this.ngZone.runOutsideAngular(() => {
                    scrollable.elementScrolled().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(onAncestorScroll);
                });
            });

            const globalScrollHandler = () => {
                this.ngZone.run(() => this.closeTooltip());
            };

            this.ngZone.runOutsideAngular(() => {
                window.addEventListener('scroll', globalScrollHandler, true);
            });
            // Clean up global listener
            this.destroyRef.onDestroy(() => {
                window.removeEventListener('scroll', globalScrollHandler, true);
            });
        }
    }

    private initializeAutoOpen(shouldAutoClose: boolean): void {
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            if (!this._overlayRef?.hasAttached()) {
                this.openTooltip();
                setTimeout(() => this._overlayRef?.updatePosition(), 0);
            }
        });
        if (shouldAutoClose) {
            this.scheduleAutoClose(this.autoCloseTime() ?? 10_000);
        }
    }

    private adjustArrowPosition(): void {
        const overlayElement = this._overlayRef?.overlayElement;
        if (!this._overlayRef) {
            return;
        }

        if (overlayElement) {
            const arrowElement = overlayElement.querySelector('.ds-tooltip-arrow');
            if (!arrowElement) {
                return;
            }
            const tooltipContent = overlayElement.querySelector('ds-tooltip-content')?.getBoundingClientRect();
            if (!tooltipContent) {
                return;
            }

            const targetRect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();

            const arrowSize = arrowElement.getBoundingClientRect();
            if (arrowElement instanceof HTMLElement) {
                if (this._position() === 'top' || this._position() === 'bottom') {
                    let leftOffset = targetRect.left + targetRect.width / 2 - tooltipContent.left - arrowSize.width / 2;
                    // We restrict arrow to stay within the overlay element
                    // A different option would be to hide arrow if it is outside. This has to be discussed with designers!
                    leftOffset = Math.max(leftOffset, ARROW_OFFSET);
                    leftOffset = Math.min(leftOffset, tooltipContent.width - CONTAINER_OFFSET - ARROW_OFFSET);

                    arrowElement.style.left = `${leftOffset}px`;
                    arrowElement.style.top = '';
                } else if (this._position() === 'left' || this._position() === 'right') {
                    const topOffset = targetRect.top + targetRect.height / 2 - tooltipContent.top - arrowSize.height / 2;
                    arrowElement.style.top = `${topOffset}px`;
                    arrowElement.style.left = '';
                }
            }
        }
    }

    private listenToOverlayState(): void {
        if (!this._overlayRef) {
            return;
        }

        this.overlaySubscriptions.add(
            (this.attachmentsSubscription = this._overlayRef.attachments().subscribe(() => {
                this.tooltipToggleChange.emit({ isTooltipOpen: true, source: 'overlayAttachment' });
            })),
        );

        this.overlaySubscriptions.add(
            (this.detachmentsSubscription = this._overlayRef.detachments().subscribe(() => {
                this._tooltipToggle.set(false); // Update signal
                this.tooltipToggleChange.emit({ isTooltipOpen: false, source: 'overlayDetachment' });
            })),
        );
    }

    private createOverlay(): OverlayRef {
        const overlayConfig = this.getOverlayConfig();
        const panelClasses: string[] = [];
        if (this.fullWidth()) {
            panelClasses.push(`ds-tooltip-full-width`, `ds-tooltip-${this._position()}`);
        }
        // Add custom class if provided
        if (this.tooltipCustomClass) {
            panelClasses.push(this.tooltipCustomClass());
        }
        return this.overlay.create({ ...overlayConfig, panelClass: panelClasses });
    }

    private getOverlayConfig(): OverlayConfig & { backdropClass?: string } {
        const scrollableAncestors = this._scrollDispatcher.getAncestorScrollContainers(this.elementRef);

        let overlayPosition = getOverlayPosition(this._position(), this.arrowPosition());

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([overlayPosition])
            .withFlexibleDimensions(false)
            .withViewportMargin(8)
            .withPush(true)
            .withScrollableContainers(scrollableAncestors);

        const scrollStrategy: ScrollStrategy = this.closeOnScroll()
            ? this.overlay.scrollStrategies.close()
            : this.overlay.scrollStrategies.reposition();

        return {
            positionStrategy,
            scrollStrategy,
            hasBackdrop: this.hasBackdrop(),
            ...(this.hasBackdrop() && { backdropClass: 'cdk-overlay-transparent-backdrop' }),
        };
    }

    private scheduleAutoClose(delay: number): void {
        if (delay === 0) {
            return;
        } // Skip auto-close if 0
        this.ngZone.runOutsideAngular(() => {
            this.autoCloseTimeout = setTimeout(() => {
                this.ngZone.run(() => {
                    if (this._overlayRef?.hasAttached()) {
                        this.closeTooltip();
                    }
                });
            }, delay);
        });
    }

    toggleTooltip(event: Event | null = null): void {
        event?.stopPropagation();

        if (this._overlayRef?.hasAttached()) {
            this._tooltipToggle.set(false);
            this.closeTooltip();
        } else {
            this._tooltipToggle.set(true);
            this.openTooltip();
        }
    }

    private openTooltip(): void {
        if (this._overlayRef?.hasAttached() || !this.content) {
            return;
        } // Prevent opening if already open or content is missing

        if (!this._overlayRef) {
            return;
        }

        const injector = Injector.create({
            providers: [
                {
                    provide: OVERLAY_REF_WITH_DATA_TOKEN,
                    useValue: {
                        ref: this._overlayRef,
                        data: { position: this._position(), arrowPosition: this.arrowPosition() },
                    },
                },
            ],
            parent: this.injector,
        });

        const tooltipPortal = new TemplatePortal(this.content, this.vcr, null, injector);
        this._overlayRef?.attach(tooltipPortal);
        this.listenForCloseOnInteraction();

        if (this.hasBackdrop()) {
            this.listenForBackdropClick();
        }
    }

    private closeTooltip(): void {
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef?.detach();
        }
        this.removeCloseOnInteractionListener();
        this.backdropSubscription?.unsubscribe();
    }

    listenForCloseOnInteraction(): void {
        if (!this.closeOnInteraction) {
            return; // Skip if CloseOnInteraction is not enabled
        }

        this.globalClickListener = this.ngZone.runOutsideAngular(() => () => {
            document.addEventListener('click', this.handleCloseOnInteraction, true);
            document.addEventListener('touchmove', this.handleCloseOnInteraction, true);
        });

        this.globalClickListener();
    }

    removeCloseOnInteractionListener(): void {
        document.removeEventListener('click', this.handleCloseOnInteraction, true);
        document.removeEventListener('touchmove', this.handleCloseOnInteraction, true);
    }

    handleCloseOnInteraction = (event: MouseEvent | TouchEvent): void => {
        const clickTarget = event.target as HTMLElement;

        if (event.type === 'touchmove') {
            this.ngZone.run(() => this.closeTooltip());
        }

        if (
            this.elementRef.nativeElement instanceof HTMLElement &&
            !this.elementRef.nativeElement.contains(clickTarget) &&
            !this._overlayRef?.overlayElement?.contains(clickTarget)
        ) {
            this.ngZone.run(() => this.closeTooltip());
        }
    };

    listenForBackdropClick(): void {
        this.backdropSubscription = this._overlayRef?.backdropClick().subscribe(() => {
            this.closeTooltip();
        });
    }

    ngAfterViewInit(): void {
        const positionStrategy = this._overlayRef?.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;

        positionStrategy.positionChanges.subscribe(() => {
            this.adjustArrowPosition();
        });

        // Adjust position initially
        this.adjustArrowPosition();
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries));
            if (this._overlayRef?.overlayElement) {
                this.resizeObserver.observe(this._overlayRef.overlayElement);
                this._lastObservedWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
            }
        });
    }

    ngOnDestroy(): void {
        this.cleanupResources();
        this.removeCloseOnInteractionListener();
        if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    private cleanupResources(): void {
        if (this.autoCloseTimeout) {
            this.ngZone.runOutsideAngular(() => clearTimeout(this.autoCloseTimeout));
        }
        this._overlayRef?.dispose();
        this.backdropSubscription?.unsubscribe();
        this.overlaySubscriptions.unsubscribe();
        this.attachmentsSubscription?.unsubscribe();
        this.detachmentsSubscription?.unsubscribe();
    }

    private onResize(entries: ResizeObserverEntry[]) {
        requestAnimationFrame(() => this.ngZone.run(() => this.handleResize(entries)));
    }

    private handleResize(entries: ResizeObserverEntry[]) {
        if (!this._tooltipToggle()) {
            return;
        }

        for (const entry of entries) {
            const newWidth = entry.contentRect.width;
            if (newWidth !== this._lastObservedWidth) {
                this._lastObservedWidth = newWidth;
                if (this._overlayRef?.hasAttached()) {
                    this._overlayRef?.updatePosition();
                    this.adjustArrowPosition();
                }
            }
        }
    }
}

/*
    Offsets: one has to move the tooltip due to arrow which is out of bounding box by size of arrow
    This is for bottom and top, offsetY, and for left and right: offsetX

    The other offset is to move full container to fit arrow head to right position.

    Instead of offsets defined here one can also use a panel class, as the overlay computation
    is taking into account offsets and client bounding rects, both should be fine to be used.
    Has to be refactored if ARROW_SIZE should be dynamically controlled.
*/
const ARROW_HEIGHT = 11;
const ARROW_WIDTH = 20;
const ARROW_OFFSET = 8;
const CONTAINER_OFFSET = ARROW_WIDTH / 2 + ARROW_OFFSET;
const TOOLTIP_POSITIONS: Record<string, ConnectedPosition> = {
    bottomstart: {
        offsetY: ARROW_HEIGHT,
        offsetX: -CONTAINER_OFFSET,
        originX: 'center',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
    },
    bottomend: {
        offsetY: ARROW_HEIGHT,
        offsetX: CONTAINER_OFFSET,
        originX: 'center',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
    },
    bottommiddle: { offsetY: ARROW_HEIGHT, originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
    topstart: {
        offsetY: -ARROW_HEIGHT,
        offsetX: -CONTAINER_OFFSET,
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
    },
    topend: {
        offsetY: -ARROW_HEIGHT,
        offsetX: CONTAINER_OFFSET,
        originX: 'center',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
    },
    topmiddle: { offsetY: -ARROW_HEIGHT, originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
    leftstart: {
        offsetX: -ARROW_HEIGHT,
        offsetY: -CONTAINER_OFFSET,
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'top',
    },
    leftend: {
        offsetX: -ARROW_HEIGHT,
        offsetY: CONTAINER_OFFSET,
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'bottom',
    },
    leftmiddle: { offsetX: -ARROW_HEIGHT, originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
    rightstart: {
        offsetX: ARROW_HEIGHT,
        offsetY: -CONTAINER_OFFSET,
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'top',
    },
    rightend: {
        offsetX: ARROW_HEIGHT,
        offsetY: CONTAINER_OFFSET,
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'bottom',
    },
    rightmiddle: { offsetX: ARROW_HEIGHT, originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
};

function getOverlayPosition(position: DsTooltipPosition, arrowPosition: DsArrowPosition): ConnectedPosition {
    const key = `${position}${arrowPosition}`; // Cast to valid key type
    return TOOLTIP_POSITIONS[key] || TOOLTIP_POSITIONS['topmiddle']; // Default fallback
}
